import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  useIonAlert,
  useIonToast,
} from '@ionic/react';
import {
  refreshOutline,
  trashBinOutline,
  checkmarkOutline,
  closeOutline,
} from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { RecycledTrack as RecycledTrackEntity } from '../../../electron/src/entity/recycledTrack';
import { once, run } from '../../helper/electron';
import './RecycleBinPage.scss';

// Helper to get filename
const getFileName = (filePath: string): string => {
  return filePath?.split('/').pop() || filePath || 'Unknown File';
};

// Helper to format date
const formatDate = (dateString: string | Date): string => {
  try {
    return new Date(dateString).toLocaleString();
  } catch (e) {
    return String(dateString);
  }
};

const PAGE_SIZE = 100;

const RecycleBinPage: React.FC = () => {
  const [recycledItems, setRecycledItems] = useState<RecycledTrackEntity[]>([]);
  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [presentToast] = useIonToast();
  const [presentAlert] = useIonAlert();
  const [processingItem, setProcessingItem] = useState<number | null>(null);
  const [selectionMode, setSelectionMode] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const isProcessingRef = React.useRef(false);

  const fetchRecycledItems = () => {
    setIsLoading(true);
    setError(null);
    console.log('Requesting recycled items...');
    run('recycle-bin/get-items');
    once(
      'recycle-bin/get-items',
      (response: {
        success: boolean;
        items?: RecycledTrackEntity[];
        totalCount?: number;
        error?: string;
      }) => {
        console.log('Received recycled items response:', response);
        if (response?.success && response.items) {
          setRecycledItems(response.items);
          const count = response.totalCount || response.items.length;
          setTotalCount(count);
          setVisibleCount(PAGE_SIZE);
          window.dispatchEvent(
            new CustomEvent('recycle-bin/badge-updated', {
              detail: { count },
            }),
          );
        } else {
          setError(response?.error || 'Failed to fetch recycled items.');
          presentToast({
            message: `Error: ${response?.error || 'Failed to load recycle bin'}`,
            duration: 3000,
            color: 'danger',
          });
        }
        setIsLoading(false);
      },
    );
  };

  useEffect(() => {
    fetchRecycledItems();
  }, []);

  const handleRestore = (itemId: number) => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    console.log('Restore item clicked:', itemId);
    setProcessingItem(itemId);

    run('recycle-bin/restore-item', { itemId });
    once(
      'recycle-bin/restore-item',
      (response: {
        success: boolean;
        restoredTrack?: any;
        remainingCount?: number;
        error?: string;
      }) => {
        console.log('Received restore response:', response);
        setProcessingItem(null);
        isProcessingRef.current = false;

        if (response?.success) {
          // Update the local list by removing the restored item
          const newItems = recycledItems.filter((item) => item.id !== itemId);
          setRecycledItems(newItems);
          const nextCount =
            response.remainingCount !== undefined
              ? response.remainingCount
              : Math.max(0, totalCount - 1);
          setTotalCount(nextCount);
          window.dispatchEvent(
            new CustomEvent('recycle-bin/badge-updated', {
              detail: { count: nextCount },
            }),
          );
          window.dispatchEvent(new CustomEvent('library-refresh-notification'));

          presentToast({
            message: 'Item restored successfully!',
            duration: 2000,
            color: 'success',
          });
        } else {
          presentToast({
            message: `Error: ${response?.error || 'Failed to restore item'}`,
            duration: 3000,
            color: 'danger',
          });
        }
      },
    );
  };

  const handleBulkRestore = async () => {
    if (isProcessingRef.current) return;
    if (selectedItems.size === 0) {
      presentToast({
        message: 'Please select items to restore',
        duration: 2000,
        color: 'warning',
      });
      return;
    }

    const itemIds = Array.from(selectedItems);
    console.log('Bulk restore clicked for items:', itemIds);

    presentAlert({
      header: 'Confirm Restore',
      message: `Are you sure you want to restore ${itemIds.length} item${itemIds.length > 1 ? 's' : ''}?`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Restore',
          handler: async () => {
            if (isProcessingRef.current) return;
            isProcessingRef.current = true;
            setIsBulkProcessing(true);
            let successCount = 0;
            let failCount = 0;

            for (const itemId of itemIds) {
              try {
                await new Promise<void>((resolve) => {
                  run('recycle-bin/restore-item', { itemId });
                  once(
                    'recycle-bin/restore-item',
                    (response: {
                      success: boolean;
                      restoredTrack?: any;
                      remainingCount?: number;
                      error?: string;
                    }) => {
                      if (response?.success) {
                        successCount++;
                      } else {
                        failCount++;
                        console.error(
                          `Failed to restore item ${itemId}:`,
                          response?.error,
                        );
                      }
                      resolve();
                    },
                  );
                });
              } catch (error) {
                failCount++;
                console.error(`Error restoring item ${itemId}:`, error);
              }
            }

            setIsBulkProcessing(false);
            isProcessingRef.current = false;
            setSelectionMode(false);
            setSelectedItems(new Set());

            if (successCount > 0) {
              window.dispatchEvent(
                new CustomEvent('library-refresh-notification'),
              );
              fetchRecycledItems();
            }

            if (successCount > 0 && failCount === 0) {
              presentToast({
                message: `Successfully restored ${successCount} item${successCount > 1 ? 's' : ''}!`,
                duration: 3000,
                color: 'success',
              });
            } else if (successCount > 0 && failCount > 0) {
              presentToast({
                message: `Restored ${successCount} item${successCount > 1 ? 's' : ''}, ${failCount} failed`,
                duration: 3000,
                color: 'warning',
              });
            } else {
              presentToast({
                message: 'Failed to restore items',
                duration: 3000,
                color: 'danger',
              });
            }
          },
        },
      ],
    });
  };

  const handleRestoreAll = () => {
    if (isProcessingRef.current) return;
    console.log('Restore all clicked');

    presentAlert({
      header: 'Restore All Items',
      message: `Are you sure you want to restore all ${totalCount} item${totalCount > 1 ? 's' : ''}?`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Restore All',
          handler: async () => {
            if (isProcessingRef.current) return;
            isProcessingRef.current = true;
            setIsBulkProcessing(true);
            try {
              const response: {
                success: boolean;
                successCount?: number;
                failedItems?: any[];
                remainingCount?: number;
                error?: string;
              } = await new Promise((resolve) => {
                run('recycle-bin/restore-all');
                once('recycle-bin/restore-all', resolve);
              });

              setIsBulkProcessing(false);
              isProcessingRef.current = false;

              if (response?.success) {
                const successCount = response.successCount || 0;
                const failedCount = response.failedItems?.length || 0;
                if (failedCount === 0) {
                  setRecycledItems([]);
                  setTotalCount(0);
                } else {
                  fetchRecycledItems();
                }

                window.dispatchEvent(
                  new CustomEvent('library-refresh-notification'),
                );
                window.dispatchEvent(
                  new CustomEvent('recycle-bin/badge-updated', {
                    detail: { count: response.remainingCount || 0 },
                  }),
                );

                if (successCount > 0 && failedCount === 0) {
                  presentToast({
                    message: `Successfully restored all ${successCount} items!`,
                    duration: 3000,
                    color: 'success',
                  });
                } else if (successCount > 0 && failedCount > 0) {
                  presentToast({
                    message: `Restored ${successCount} items, ${failedCount} failed`,
                    duration: 3000,
                    color: 'warning',
                  });
                } else {
                  presentToast({
                    message: 'No items were restored',
                    duration: 3000,
                    color: 'warning',
                  });
                }
              } else {
                fetchRecycledItems();
                presentToast({
                  message: response?.error || 'Failed to restore items',
                  duration: 3000,
                  color: 'danger',
                });
              }
            } catch (error: any) {
              setIsBulkProcessing(false);
              isProcessingRef.current = false;
              fetchRecycledItems();
              console.error(`Error restoring all items:`, error);
              presentToast({
                message: error.message || 'Failed to restore items',
                duration: 3000,
                color: 'danger',
              });
            }
          },
        },
      ],
    });
  };

  const handleDelete = (itemId: number) => {
    if (isProcessingRef.current) return;
    console.log('Delete permanently clicked:', itemId);

    presentAlert({
      header: 'Confirm Deletion',
      message:
        'Are you sure you want to permanently delete this item? This action cannot be undone.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Delete',
          role: 'destructive',
          handler: () => {
            if (isProcessingRef.current) return;
            isProcessingRef.current = true;
            setProcessingItem(itemId);

            run('recycle-bin/delete-item', { itemId });
            once(
              'recycle-bin/delete-item',
              (response: {
                success: boolean;
                remainingCount?: number;
                error?: string;
              }) => {
                console.log('Received delete response:', response);
                setProcessingItem(null);
                isProcessingRef.current = false;

                if (response?.success) {
                  // Update the local list by removing the deleted item
                  const newItems = recycledItems.filter(
                    (item) => item.id !== itemId,
                  );
                  setRecycledItems(newItems);
                  const nextCount =
                    response.remainingCount !== undefined
                      ? response.remainingCount
                      : Math.max(0, totalCount - 1);
                  setTotalCount(nextCount);
                  window.dispatchEvent(
                    new CustomEvent('recycle-bin/badge-updated', {
                      detail: { count: nextCount },
                    }),
                  );

                  presentToast({
                    message: 'Item deleted permanently.',
                    duration: 2000,
                    color: 'primary',
                  });
                } else {
                  presentToast({
                    message: `Error: ${
                      response?.error || 'Failed to delete item'
                    }`,
                    duration: 3000,
                    color: 'danger',
                  });
                }
              },
            );
          },
        },
      ],
    });
  };

  const handleEmptyBin = () => {
    if (isProcessingRef.current) return;
    console.log('Empty bin clicked');

    presentAlert({
      header: 'Empty Recycle Bin',
      message:
        'Are you sure you want to permanently delete ALL items? This action cannot be undone.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Empty Bin',
          role: 'destructive',
          handler: () => {
            if (isProcessingRef.current) return;
            isProcessingRef.current = true;
            setIsLoading(true);

            run('recycle-bin/empty-bin');
            once(
              'recycle-bin/empty-bin',
              (response: {
                success: boolean;
                count?: number;
                error?: string;
              }) => {
                console.log('Received empty bin response:', response);
                setIsLoading(false);
                isProcessingRef.current = false;

                if (response?.success) {
                  setRecycledItems([]);
                  setTotalCount(0);
                  window.dispatchEvent(
                    new CustomEvent('recycle-bin/badge-updated', {
                      detail: { count: 0 },
                    }),
                  );

                  presentToast({
                    message: `Recycle bin emptied. ${
                      response.count || 0
                    } items removed.`,
                    duration: 2000,
                    color: 'primary',
                  });
                } else {
                  presentToast({
                    message: `Error: ${
                      response?.error || 'Failed to empty recycle bin'
                    }`,
                    duration: 3000,
                    color: 'danger',
                  });
                }
              },
            );
          },
        },
      ],
    });
  };

  const toggleSelection = (itemId: number) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === recycledItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(recycledItems.map((item) => item.id)));
    }
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedItems(new Set());
  };

  const renderContent = () => {
    if (isLoading || isBulkProcessing) {
      return (
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <IonSpinner name="crescent" />
          {isBulkProcessing && (
            <IonText color="medium">
              <p>Processing items...</p>
            </IonText>
          )}
        </div>
      );
    }
    if (error) {
      return (
        <IonText color="danger">
          <p>Error loading items: {error}</p>
        </IonText>
      );
    }
    if (recycledItems.length === 0) {
      return (
        <div className="empty-state-container">
          <div className="empty-state-icon-wrapper">
            <IonIcon icon={trashBinOutline} className="empty-state-icon" />
          </div>
          <h2 className="empty-state-title">Recycle Bin is Empty</h2>
          <p className="empty-state-subtitle">
            Tracks you remove from your library will appear here. You can
            restore them at any time or delete them permanently to free up
            space.
          </p>
        </div>
      );
    }

    const itemsToRender = recycledItems.slice(0, visibleCount);

    return (
      <>
        {selectionMode && (
          <IonItem lines="none" style={{ marginBottom: '10px' }}>
            <IonLabel>
              <strong>{selectedItems.size}</strong> item
              {selectedItems.size !== 1 ? 's' : ''} selected
            </IonLabel>
            <IonButton
              slot="end"
              fill="clear"
              size="small"
              onClick={toggleSelectAll}
            >
              {selectedItems.size === recycledItems.length
                ? 'Deselect All'
                : 'Select All'}
            </IonButton>
          </IonItem>
        )}
        <IonList>
          {itemsToRender.map((item) => (
            <IonItem
              key={item.id}
              lines="full"
              button={selectionMode}
              onClick={() => selectionMode && toggleSelection(item.id)}
              style={{ cursor: selectionMode ? 'pointer' : 'default' }}
            >
              {selectionMode && (
                <IonCheckbox
                  slot="start"
                  checked={selectedItems.has(item.id)}
                />
              )}
              <IonLabel>
                <h2>{getFileName(item.originalTrackData.filePath)}</h2>
                <p>
                  <IonNote color="medium">
                    Original Path: {item.originalTrackData.filePath}
                  </IonNote>
                </p>
                <p>
                  <IonNote color="medium">
                    Moved On: {formatDate(item.movedAt)}
                  </IonNote>
                </p>
              </IonLabel>
              {!selectionMode && (
                <IonButtons slot="end">
                  {processingItem === item.id ? (
                    <IonSpinner name="dots" />
                  ) : (
                    <>
                      <IonButton
                        fill="clear"
                        color="success"
                        onClick={() => handleRestore(item.id)}
                        title="Restore"
                      >
                        <IonIcon slot="icon-only" icon={refreshOutline} />
                      </IonButton>
                      <IonButton
                        fill="clear"
                        color="danger"
                        onClick={() => handleDelete(item.id)}
                        title="Delete Permanently"
                      >
                        <IonIcon slot="icon-only" icon={trashBinOutline} />
                      </IonButton>
                    </>
                  )}
                </IonButtons>
              )}
            </IonItem>
          ))}
          {visibleCount < recycledItems.length && (
            <IonItem lines="none">
              <IonButton
                expand="block"
                fill="outline"
                onClick={() => {
                  setVisibleCount((prev) => prev + PAGE_SIZE);
                }}
              >
                Load more
              </IonButton>
            </IonItem>
          )}
        </IonList>
      </>
    );
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            {selectionMode ? (
              <IonButton
                onClick={exitSelectionMode}
                disabled={isBulkProcessing || isLoading}
              >
                <IonIcon slot="start" icon={closeOutline} />
                Cancel
              </IonButton>
            ) : (
              <IonBackButton
                defaultHref="/"
                disabled={isBulkProcessing || isLoading}
              />
            )}
          </IonButtons>
          <IonTitle>
            {selectionMode ? `${selectedItems.size} Selected` : 'Recycle Bin'}
          </IonTitle>
          {recycledItems.length > 0 && !isLoading && (
            <IonButtons slot="end">
              {selectionMode ? (
                <IonButton
                  color="success"
                  onClick={handleBulkRestore}
                  disabled={
                    selectedItems.size === 0 || isBulkProcessing || isLoading
                  }
                >
                  <IonIcon slot="start" icon={checkmarkOutline} />
                  Restore
                </IonButton>
              ) : (
                <>
                  <IonButton
                    onClick={() => setSelectionMode(true)}
                    disabled={isBulkProcessing || isLoading}
                  >
                    Select
                  </IonButton>
                  <IonButton
                    color="success"
                    onClick={handleRestoreAll}
                    disabled={isBulkProcessing || isLoading}
                  >
                    Restore All
                  </IonButton>
                  <IonButton
                    color="danger"
                    onClick={handleEmptyBin}
                    disabled={isBulkProcessing || isLoading}
                  >
                    Empty Bin
                  </IonButton>
                </>
              )}
            </IonButtons>
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {renderContent()}
      </IonContent>
    </IonPage>
  );
};

export default RecycleBinPage;
